import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementVNode as _createElementVNode, unref as _unref, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "sfx-tooltip-text text-body1 text-center relative-position" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { key: 1 }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "sfx-tooltip-arrow absolute" }, null, -1)
const _hoisted_5 = { class: "sfx-tooltip-text text-body1 text-center relative-position" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "sfx-tooltip-arrow absolute" }, null, -1)

import { Platform, QTooltip } from "quasar"
import { TooltipData, TooltipPosition } from "src/util/types/components/sfxTooltip"
import { computed, getCurrentInstance, onMounted, onUnmounted, PropType, ref, watch } from "vue"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxTooltip',
  props: {
  data: {
    type: Object as PropType<TooltipData>,
  },

  position: {
    type: String as PropType<TooltipPosition>,
    default: "top",
  },

  isOpen: {
    type: null as unknown as PropType<boolean | null>,
  },
},
  emits: ["update:isOpen"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const arrowLocation = ref()
const anchor = ref()
const self = ref()

const model = computed({
  get() {
    return props.isOpen != null ? props.isOpen : null
  },

  set(value) {
    return emit("update:isOpen", value)
  },
})

const mobileModelRef = ref(false)

const modelMobile = computed({
  get() {
    // If we have set the isOpen state, use that, else use the internal ref model
    return props.isOpen != null ? props.isOpen : mobileModelRef.value
  },

  set(value) {
    // If we have provided isOpen, update it, else, update only the internal ref model
    if (props.isOpen != null) {
      return emit("update:isOpen", value)
    } else {
      mobileModelRef.value = value
    }
  },
})

const parentEl = ref()

// We want to be able to detect when the tooltip is currently showing, so that we know when to close it on outside clicks.
// We cannot make use of the model, since it is updated before the tooltip is shown and closing the tooltip would be immediate.
// The @show and @hide emits on q-toolbar, however, are triggered after.
// Due to that fact, we can easily have this small ref and use that for determining when to close the tooltip.
const tooltipShown = ref<boolean | null>(Platform.is.mobile ? modelMobile.value : model.value)

//endregion

//region Lifecycle

if (Platform.is.mobile) {
  // We want to manually open the tooltip on mobile on click of the parent element.
  // So we get the current instance, then traverse the element tree to the parent and attach a listener to it
  // Once the component dies, we of course remove the listener, so we don't create memory leaks

  onMounted(() => {
    const instance = getCurrentInstance()
    parentEl.value = instance?.parent?.subTree.el
    parentEl.value?.addEventListener("click", openTooltipInternally)
  })

  onUnmounted(() => {
    parentEl.value?.removeEventListener("click", openTooltipInternally)
  })
}

//endregion

//region Functions

function setTooltipPositionProperties(position: TooltipPosition) {
  switch (position) {
    case "top": {
      arrowLocation.value = "bottom"
      anchor.value = "top middle"
      self.value = "bottom middle"
      break
    }
    case "top-right": {
      arrowLocation.value = "bottom-left"
      anchor.value = "top right"
      self.value = "bottom middle"
      break
    }
    case "top-left": {
      arrowLocation.value = "bottom-right"
      anchor.value = "top left"
      self.value = "bottom middle"
      break
    }
    case "bottom": {
      arrowLocation.value = "top"
      anchor.value = "bottom middle"
      self.value = "top middle"
      break
    }
    case "bottom-right": {
      arrowLocation.value = "top-left"
      anchor.value = "bottom right"
      self.value = "top middle"
      break
    }
    case "bottom-left": {
      arrowLocation.value = "top-right"
      anchor.value = "bottom left"
      self.value = "top middle"
      break
    }
    case "right": {
      arrowLocation.value = "left"
      anchor.value = "center end"
      self.value = "center start"
      break
    }
    case "left": {
      arrowLocation.value = "right"
      anchor.value = "center start"
      self.value = "center end"
      break
    }
  }
}

function closeTooltip() {
  if (tooltipShown.value) {
    model.value = false
    modelMobile.value = false
  }
}

function setShown(isShown: boolean) {
  tooltipShown.value = isShown
}

// Mobile only function
function openTooltipInternally() {
  mobileModelRef.value = true
}

//endregion

//region Setup Code

watch(
  () => props.position,
  (position) => {
    setTooltipPositionProperties(position)
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_ctx.$q.platform.is.mobile)
    ? (_openBlock(), _createBlock(_unref(QTooltip), _mergeProps({
        key: 0,
        modelValue: modelMobile.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelMobile).value = $event)),
        class: ["sfx-tooltip", `sfx-tooltip-arrow-${arrowLocation.value}`],
        anchor: anchor.value,
        self: self.value
      }, _ctx.$attrs, {
        onShow: _cache[1] || (_cache[1] = ($event: any) => (setShown(true))),
        onHide: _cache[2] || (_cache[2] = ($event: any) => (setShown(false)))
      }), {
        default: _withCtx(() => [
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
            _renderSlot(_ctx.$slots, "default", {}, () => [
              (props.data)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (props.data.isHtml)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          innerHTML: props.data.text
                        }, null, 8, _hoisted_2))
                      : (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(props.data.text), 1))
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ])), [
            [_directive_click_outside, closeTooltip]
          ]),
          _hoisted_4
        ]),
        _: 3
      }, 16, ["modelValue", "class", "anchor", "self"]))
    : (_openBlock(), _createBlock(_unref(QTooltip), _mergeProps({
        key: 1,
        modelValue: model.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((model).value = $event)),
        class: ["sfx-tooltip", `sfx-tooltip-arrow-${arrowLocation.value}`],
        anchor: anchor.value,
        self: self.value
      }, _ctx.$attrs, {
        onShow: _cache[4] || (_cache[4] = ($event: any) => (setShown(true))),
        onHide: _cache[5] || (_cache[5] = ($event: any) => (setShown(false)))
      }), {
        default: _withCtx(() => [
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_5, [
            _renderSlot(_ctx.$slots, "default", {}, () => [
              (props.data)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (props.data.isHtml)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          innerHTML: props.data.text
                        }, null, 8, _hoisted_6))
                      : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(props.data.text), 1))
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ])), [
            [_directive_click_outside, closeTooltip]
          ]),
          _hoisted_8
        ]),
        _: 3
      }, 16, ["modelValue", "class", "anchor", "self"]))
}
}

})